import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Me } from "../../api/users";
import { PostGet } from "../../api/post";
import { GetAcativeRestaurants } from "../../api/restaurant";
import { listServiceNotes } from "../../api/service_note";
import { TYLevel } from "../../constants";

function formatDate(date) {
  return date.toISOString().slice(0, 10);
}

function getMonthStartDate() {
  const date = new Date();
  date.setDate(1);
  return formatDate(date);
}

function getTodayDate() {
  return formatDate(new Date());
}

const STATUS_OPTIONS = [
  { value: 0, label: "Все статусы" },
  { value: 1, label: "Не согласовано" },
  { value: 2, label: "На доработке" },
  { value: 3, label: "Согласовано" },
  { value: 4, label: "Отклонено" },
];

function statusBadgeClass(color) {
  if (color === "green") return "bg-success";
  if (color === "yellow") return "bg-warning text-dark";
  if (color === "red") return "bg-danger";
  return "bg-secondary";
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  return date.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatWeek(startDate, endDate) {
  if (!startDate || !endDate) return "—";
  return `${startDate} — ${endDate}`;
}

const ServiceNotesReport = ({ token }) => {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(getMonthStartDate());
  const [endDate, setEndDate] = useState(getTodayDate());
  const [restaurantId, setRestaurantId] = useState(0);
  const [statusId, setStatusId] = useState(0);
  const [restaurantList, setRestaurantList] = useState([]);
  const [accessLevel, setAccessLevel] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(false);

  const canPickRestaurant = accessLevel >= TYLevel;

  useEffect(() => {
    async function init() {
      try {
        const user = await Me(token);
        const post = await PostGet(token, user.post_id);
        setAccessLevel(post.access_level);
        if (post.access_level >= TYLevel) {
          const restaurants = await GetAcativeRestaurants(token);
          setRestaurantList(restaurants || []);
        }
      } catch (err) {
        console.error(err);
        setError("Не удалось загрузить профиль");
      }
    }
    init();
  }, [token]);

  async function loadNotes() {
    if (!startDate || !endDate) {
      setError("Укажите начало и конец периода");
      return;
    }
    if (startDate > endDate) {
      setError("Дата начала не может быть позже даты окончания");
      return;
    }

    setError("");
    setLoading(true);
    try {
      const data = await listServiceNotes(token, {
        start_date: startDate,
        end_date: endDate,
        restaurant_id: canPickRestaurant ? restaurantId : 0,
        status_id: statusId,
      });
      if (data?.detail) {
        setError(data.detail);
        setItems([]);
        return;
      }
      setItems(Array.isArray(data) ? data : []);
      setLoaded(true);
    } catch (err) {
      console.error(err);
      setError("Не удалось загрузить служебные записки");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="reports-page container">
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/reports">Отчёты</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            Служебные записки
          </li>
        </ol>
      </nav>

      <h1>Служебные записки</h1>
      <p className="text-muted">
        Изменения расписания ЧК после согласования ТУ
      </p>

      <div className="card shadow-sm border-0 mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-3 col-sm-6">
              <label className="form-label">Период с</label>
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="col-md-3 col-sm-6">
              <label className="form-label">Период по</label>
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            {canPickRestaurant && (
              <div className="col-md-3 col-sm-6">
                <label className="form-label">Ресторан</label>
                <select
                  className="form-select"
                  value={restaurantId}
                  onChange={(e) => setRestaurantId(Number(e.target.value))}
                >
                  <option value={0}>Все рестораны</option>
                  {restaurantList.map((restaurant) => (
                    <option key={restaurant.id} value={restaurant.id}>
                      {restaurant.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="col-md-3 col-sm-6">
              <label className="form-label">Статус</label>
              <select
                className="form-select"
                value={statusId}
                onChange={(e) => setStatusId(Number(e.target.value))}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3 col-sm-6">
              <button
                type="button"
                className="btn w-100"
                onClick={loadNotes}
                disabled={loading}
              >
                {loading ? "Загрузка…" : "Показать"}
              </button>
            </div>
          </div>
          {error && <p className="text-danger mt-3 mb-0">{error}</p>}
        </div>
      </div>

      {loaded && (
        <div className="card shadow-sm border-0">
          <div className="card-body p-0">
            {items.length === 0 ? (
              <p className="text-muted p-4 mb-0">
                За выбранный период служебных записок не найдено
              </p>
            ) : (
              <div className="table-responsive">
                <table className="table table-bordered table-hover mb-0 service-notes-table">
                  <thead className="table-light">
                    <tr className="text-center">
                      <th>№</th>
                      <th>Создана</th>
                      <th>Ресторан</th>
                      <th>Неделя</th>
                      <th>Операция</th>
                      <th>Статус</th>
                      <th>Директор</th>
                      <th>Комментарий</th>
                      <th>ТУ</th>
                      <th>Применено</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr
                        key={item.id}
                        className="service-note-row-link"
                        onClick={() => navigate(`/report-service-notes/${item.id}`)}
                        style={{ cursor: "pointer" }}
                      >
                        <td className="text-center">
                          <Link
                            to={`/report-service-notes/${item.id}`}
                            className="service-note-link"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {item.id}
                          </Link>
                        </td>
                        <td className="text-nowrap">{formatDateTime(item.created)}</td>
                        <td>{item.restaurant_name}</td>
                        <td className="text-nowrap">
                          {formatWeek(item.start_date, item.end_date)}
                        </td>
                        <td>{item.operation_type_label || item.operation_type || "—"}</td>
                        <td className="text-center">
                          <span
                            className={`badge ${statusBadgeClass(item.status_color)}`}
                          >
                            {item.status_name}
                          </span>
                        </td>
                        <td>{item.creater_name}</td>
                        <td className="service-note-comment">{item.comment || "—"}</td>
                        <td>{item.TY_surname_name || "—"}</td>
                        <td className="text-center text-nowrap">
                          {item.applied_at ? (
                            <span className="badge bg-success">Да</span>
                          ) : item.apply_error ? (
                            <span
                              className="badge bg-danger"
                              title={item.apply_error}
                            >
                              Ошибка
                            </span>
                          ) : (
                            <span className="text-muted">Нет</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceNotesReport;
